import React, { useEffect, useRef } from 'react';
import BrandLogo from 'tailwind-components/BrandLogo/BrandLogo';
import { cn } from 'lib/utils';
import { shallowEqual, useDispatch, useSelector } from 'react-redux';
import propTypes from 'prop-types';
import ToggleSideBar from '../../components/layout/ToggleSideBar/ToggleSideBar';
import { toggleSidebar } from 'state/actions/site';
import SidebarContent from './SidebarContent';
import useMobile, { MOBILE_BREAKPOINT } from 'hooks/useMobile';
import { getCompany } from 'helpers/getCompanyVariables';
import { usePortalWalkthrough } from 'tailwind-components/PortalWalkthrough/PortalWalkthroughContext';

const company = getCompany();
const isSunshine = company === 'sunshine';

function Sidebar({ activePage }) {
  const { isActive: isTourActive } = usePortalWalkthrough() || {};
  const sidebarExpanded = useSelector(
    state => state.site.sidebarExpanded,
    shallowEqual
  );
  const dispatch = useDispatch();
  const isMobile = useMobile();
  const hasInitializedSidebar = useRef(false);

  useEffect(() => {
    if (hasInitializedSidebar.current) return;
    hasInitializedSidebar.current = true;
    if (globalThis.innerWidth >= MOBILE_BREAKPOINT) {
      dispatch(toggleSidebar(true));
    }
  }, [dispatch]);

  const effectiveExpanded = sidebarExpanded || !isMobile;

  const isShrinked = false;

  const handleCloseSidebarMobile = () => {
    dispatch(toggleSidebar(false));
  };

  return (
    <div
      className={cn(
        'h-full z-50',
        'max-md:fixed max-md:top-0 max-md:left-0 max-md:z-60 max-md:mr-0 max-md:h-full',
        !sidebarExpanded && isMobile && 'max-md:pointer-events-none'
      )}
    >
      <div
        className={cn(
          'w-full h-screen fixed top-0 left-0 bg-black/20 z-40 hidden',
          sidebarExpanded && isMobile ? 'max-md:block' : 'max-md:hidden'
        )}
        onClick={isTourActive ? undefined : handleCloseSidebarMobile}
        aria-hidden='true'
      />
      <div
        className={cn(
          'min-w-[285px] rounded-[10px] py-4 h-full relative',
          isSunshine ? 'bg-primary' : 'bg-white',
          'max-md:w-[270px] max-md:rounded-none max-md:z-60 max-md:shadow-xl',
          'max-md:transition-transform max-md:duration-300 max-md:ease-in-out',
          // Use only sidebarExpanded for position so initial paint on mobile is already closed (no flash)
          sidebarExpanded ? 'max-md:translate-x-0' : 'max-md:-translate-x-full',
          isTourActive && 'pointer-events-none opacity-60'
        )}
        {...(isTourActive ? { inert: true } : {})}
        aria-hidden={isTourActive || undefined}
      >
        <div
          className={cn(
            'flex items-center justify-center leading-[0.5] select-none',
            // the mobile toggle icon strokes with currentColor, so it needs the panel's contrast color
            isSunshine ? 'mb-2 text-white' : 'mb-5',
            isSunshine &&
              effectiveExpanded &&
              isMobile &&
              'justify-between pr-4 pl-[10px]',
            !isSunshine && effectiveExpanded && 'justify-between pl-5 pr-4',
            !effectiveExpanded && 'justify-center p-0'
          )}
        >
          {isMobile && <ToggleSideBar />}
          {effectiveExpanded && (
            <BrandLogo
              variant='fullLogo'
              // the agency logo only ever renders under sunshine, whose panel is navy
              tone='onDark'
              alt=''
              // sunshine's bundled lockup is as wide as an agency logo, so both are
              // capped on height and given room sideways. agency logos never render
              // under oneill, whose own mark is sized by width instead
              className={cn(
                isSunshine
                  ? 'h-[56px] w-auto max-w-[180px]'
                  : 'w-[190px] h-auto mx-auto'
              )}
            />
          )}
        </div>

        <SidebarContent activePage={activePage} isShrinked={isShrinked} />
      </div>
    </div>
  );
}

Sidebar.propTypes = {
  activePage: propTypes.string
};

export default Sidebar;
